import {
    BlurEvent,
    Button,
    ChangeEvent,
    ClickEvent,
    DataDisplayEvent,
    LookupModelSelectionEvent, Panel,
    ScreenStack,
    Snackbar,
    Textbox
} from "@mcleod/components";
import {Alignment, Api, Currency, ModelRow, StringUtil} from "@mcleod/core";
import { getDispatchControlBoolean, LMEGeneralSettings } from "@mcleod/general";
import { CarrierAssignmentStepper, OfferField, RowCarrierOffer } from "@mcleod/powerbroker";
import { AutogenLayoutQuickOfferSlideout } from "./autogen/AutogenLayoutQuickOfferSlideout";
import { CommonTooltips } from "@mcleod/common";

export type CarrierOfferInfo = {
    payee_id: string,
    carrier_name?: string,
    icc_number?: string,
    dot_number?: string,
    carrier_contact?: string,
    carrier_phone?: string,
    carrier_email?: string,
    dispatch_offer?: Currency,
    carrier_empty_from_city_id?: number,
    broker_avail_tract?: string,
    counter_offer?: Currency,
    carrier_rate?: Currency,
    rating_star_count?: number
}

export class QuickOfferSlideout extends AutogenLayoutQuickOfferSlideout {
    static readonly MC_NUMBER = "M";
    static readonly DOT = "D";
    static readonly INTRASTATE = "C";

    public assignButtonClicked: () => void;
    public carrierAssigned: () => void;
    public assignableCheck: () => void;

    override onLoad() {
        this.tbCarrierName.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.carrierSelected(event));
        this.tbContactName.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.contactSelected(event));

        this.loCarrierPay.mainDataSource.addDisplayListener((event) => {
            this.setupBuyToMarket();
            this.updateBuyToMarket();
        });
        this.loCarrierPay.buttonOverrideTargetPay.visible = false;
        this.loCarrierPay.buttonOverrideMaxPay.visible = false;
    }


    async displayPrequalResult(fieldName: string, value: string) {
        this.layoutCarrierPrequalResult.hidePrequalPanels();
        if (StringUtil.isEmptyString(this.activeRow.get("payee_id"))) {
            const response = await this.layoutCarrierPrequalResult.prequalCarrier(fieldName, value)
            const qualificationResult = response?.[0];
            if(qualificationResult?.carrier_watch_vendor){
                this.setLabelCarrierInvited(this.panelCarrier, qualificationResult?.carrier_invited, qualificationResult?.dot_number, qualificationResult?.mc_number);
            }
        }
    }

    private setLabelCarrierInvited(panel: Panel, visible: boolean, dot_number: string, mc_number: string) {
        panel.forEveryChildComponent(comp => {
            if (StringUtil.equalsIgnoreCase("labelPendingInvite",comp.id)) {
                comp.visible = visible;
                if(visible) {
                    CommonTooltips.setTooltipContentLayout(comp, null, "lme/powerbroker/CarrierInvitedQuickInfo", {
                            minWidth: 100,
                            minHeight: 100
                        },
                        {dot_number: dot_number, mc_number:mc_number}
                    );
                    comp.tooltipDynamicOptions = { position: Alignment.RIGHT };
                }
            }
        });
    }

    setupBuyToMarket() {
        const move = this.loCarrierPay.mainDataSource.activeRow;
        const brokerage = move?.getBoolean('brokerage');
        const btmExclude = move?.getBoolean('buy_to_market_ex');
        this.panelBuyToMarket.visible =
            getDispatchControlBoolean("show_btm", false)
            && brokerage
            && !btmExclude;
    }

    async calculateCarrierRate(modelRow: ModelRow) {
        const response = await Api.search("lme/dispatch/calculate-override-pay", {
            "movement_id": this.loCarrierPay.mainDataSource.activeRow.get("id"),
            "order_id": this.loCarrierPay.mainDataSource.activeRow.get("orders.id"),
            "payee_id": modelRow.get("payee_id"),
            "auto_rate": LMEGeneralSettings.getSingleton().getDispatchControl().rate_carrier == "Y",
            "calculate_fuel_surcharge": true
        });
        if (response.data[0]?.carrier_rate_lane_id) {
            //Had to create new currency for each because of passing by reference issues
            const amount = new Currency(response.data[0]?.total_pay);
            const carrier_rate = new Currency(response.data[0]?.total_pay);
            this.activeRow.set("amount", amount);
            this.activeRow.set("carrier_rate", carrier_rate);
            this.activeRow.set("carrier_rate_id", response.data[0]?.carrier_rate_id);
            this.activeRow.set("carrier_rate_lane_id", response.data[0]?.carrier_rate_lane_id);
            if (response.data[0]?.carrier_contact || response.data[0]?.carrier_email || response.data[0]?.carrier_phone) {
                this.activeRow.setValueAndLookupModelData("contact_name", response.data[0]?.carrier_contact, { "name": response.data[0]?.carrier_contact });
                this.activeRow.set("email", response.data[0]?.carrier_email);
                this.activeRow.set("contact_phone", response.data[0]?.carrier_phone);
            }
        } else {
            this.activeRow.set("carrier_rate", null);
        }
    }

    private carrierSelected(event: LookupModelSelectionEvent) {
        const tbCarrierName = event.target as Textbox;
        if (event.hasSelection() !== true) {
            if (StringUtil.isEmptyString(tbCarrierName.getDataValue())) {
                this.clearCarrierAndRate();
            }
            return;
        }
        const selectedRow = event.selection;
        this.activeRow.setValues({
            "carrier_name": selectedRow.get("name"),
            "payee_id": selectedRow.get("id"),
            "icc_number": selectedRow.get("icc_number"),
            "dot_number": selectedRow.get("dot_number"),
            "intrastate_code": selectedRow.get("intrastate_code"),
            "contact_name": selectedRow.get("contact.name"),
            "contact_phone": selectedRow.get("contact.phone", selectedRow.get("phone_number")),
            "email": selectedRow.get("contact.email"),
            "rating_star_count": selectedRow.get("rating_star_count")
        });
        this.displayRatingStar(this.activeRow);
        this.activeRow.setLookupModelData("contact_name", { "name": selectedRow.get("contact.name") });
        this.activeRow.setLookupModelData("carrier_name", { "name": selectedRow.get("name") });
        this.mainDataSource.displayDataInBoundComponents();
        this.calculateCarrierRate(this.activeRow).then(() => this.afterAmountUpdated());
    }

    public displayRatingStar(row: ModelRow) {
        this.starratingCarrier.visible = row.get("rating_star_count") != null;
    }

    private contactSelected(event: LookupModelSelectionEvent) {
        if (event.hasSelection() !== true)
            return;
        const selectedRow = event.selection;
        this.activeRow.setValues({
            "contact_name": selectedRow.get("name"),
            "contact_phone": selectedRow.get("phone"),
            "email": selectedRow.get("email")
        });
        this.mainDataSource.displayDataInBoundComponents();
    }

    public updateMargins(offerField: OfferField, marginField: string, marginTextbox: Textbox, percentTextbox: Textbox, blockSnackbar: boolean = false) {
        RowCarrierOffer.getMarginData(this.mainDataSource.activeRow, offerField).then(marginData => {
            if (!marginData) return;
            this.mainDataSource.activeRow.set(marginField, marginData.margin);
            marginTextbox.displayData(this.mainDataSource.activeRow, this.mainDataSource.data, this.mainDataSource.rowIndex);
            percentTextbox.text = marginData.margin_pct ? `${Math.round(marginData.margin_pct)}%` : "--";
        }).catch(error => {
            if (error.name === "nullTotalCharge") {
                if (!blockSnackbar) Snackbar.showWarningSnackbar(error.message)
            } else if (error.name === "nullOffer") {
                marginTextbox.text = "--";
                percentTextbox.text = "--";
                return;
            } else {
                throw error;
            }
        });
    }

    private async _findSetCarrier(icc_number, dot_number, intrastate_code) {
        if (!icc_number && !dot_number && !intrastate_code) return;
        const response = await Api.search("lme/dispatch/carrier", icc_number ? { icc_number } : dot_number ? { dot_number } : { intrastate_code });
        const carrierRow = response?.data?.[0];
        if (carrierRow) {
            this.mainDataSource.activeRow.setLookupModelData("carrier_name",
                new ModelRow(this.tbCarrierName.lookupModel, false, { name: carrierRow["payee.name"] }))
            this.mainDataSource.activeRow.setLookupModelData("contact_name",
                new ModelRow(this.tbContactName.lookupModel, false, { name: carrierRow["contact.name"] }))
            this.mainDataSource.activeRow.setValues({
                "carrier_name": carrierRow["payee.name"],
                "payee_id": carrierRow["id"],
                "icc_number": carrierRow["icc_number"],
                "intrastate_code": carrierRow["intrastate_code"],
                "dot_number": carrierRow["dot_number"],
                "contact_name": carrierRow["contact.name"],
                "contact_phone": carrierRow["contact.phone"],
                "email": carrierRow["contact.email"]
            })
            this.mainDataSource.displayDataInBoundComponents();
        }
    }

    textboxAmountOnBlur(event: BlurEvent) {
        this.afterAmountUpdated();
    }

    afterAmountUpdated() {
        this.updateMargins(OfferField.OFFER, "offer_margin", this.tbDispatchMargin, this.tbDispatchMarginPrcnt);
        if (this.textboxBuyToMarket.visible) {
            this.updateBuyToMarket();
        }
        const amount = this.mainDataSource.activeRow.getCurrency("amount");
        const carrierRate = this.mainDataSource.activeRow.getCurrency("carrier_rate_calc_pay", this.mainDataSource.activeRow.getCurrency("carrier_rate"));
        if (amount != null && !this.activeRow.isNull("carrier_rate_lane_id") && amount.getAmount() == carrierRate?.getAmount()) {
            this.labelAutoRated.visible = true;
            CommonTooltips.setTooltipContentLayout(
                this.labelAutoRated,
                this.mainDataSource.activeRow.get("carrier_rate_lane_id"),
                "lme/dispatch/rates/CarrierLaneRateQuickInfo"
            );
        } else {
            this.labelAutoRated.visible = false;
            this.labelAutoRated.tooltip = null;
            this.activeRow.set("carrier_rate_id", null);
            this.activeRow.set("carrier_rate_lane_id", null);
        }
    }

    textboxCounterOfferOnBlur(event: BlurEvent) {
        this.updateMargins(OfferField.COUNTER, "counter_margin", this.tbCounterMargin, this.tbCounterMarginPrcnt);
        if (this.textboxBuyToMarket.visible) {
            this.updateBuyToMarket();
        }
    }

    public async updateBuyToMarket() {
        const targetPay = this.loCarrierPay.activeRow?.getCurrency("target_pay_calc");
        const carrierPay = this.activeRow?.getCurrency("counter_offer", this.activeRow?.getCurrency("amount"));
        if (carrierPay == null || targetPay == null || carrierPay.isZero() || targetPay.isZero()) {
            this.activeRow.set("buy_to_market", null);
            this.activeRow.set("buy_to_market_percent", null);
            this.textboxBuyToMarketPercent.color = this.textboxBuyToMarket.color = "default";
        } else {
            const buyToMarket: number = await carrierPay.subtract(targetPay);
            const isOverMarket: boolean = buyToMarket > 0;
            this.activeRow.set("buy_to_market", (isOverMarket ? "OVER " : "") + "$" + buyToMarket.toFixed(2));
            const buyToMarketPercent = (buyToMarket / targetPay.getAmount()) * 100;
            this.activeRow.set("buy_to_market_percent", (isOverMarket ? "OVER " : "") + buyToMarketPercent.toFixed(2) + "%");
            const btmColor = isOverMarket ? "error" : "success";
            this.textboxBuyToMarketPercent.color = btmColor;
            this.textboxBuyToMarket.color = btmColor;
        }
        this.textboxBuyToMarket.displayData(this.activeRow, null, 0);
        this.textboxBuyToMarketPercent.displayData(this.activeRow, null, 0);
        this.panelBuyToMarket.marginLeft = !this.activeRow.isNull("counter_offer") ? 435 : null;
    }

    tbContactNameBeforeLookupModelSearch(event) {
        const carrierId = (this.mainDataSource?.activeRow?.getFirstLookupModelData("carrier_name") as ModelRow)?.get("id") || this.mainDataSource?.activeRow?.get("payee_id");
        if (carrierId) {
            event.filter.parent_row_type = "P";
            event.filter.parent_row_id = carrierId;
            event.filter.is_active = "Y";
        } else {
            event.preventDefault();
        }
    }

    tbCarrierNameOnChange(event: ChangeEvent) {
        if (StringUtil.isEmptyString(event.newValue)) {
            this.clearCarrierAndRate();
        }
        this.mainDataSource.activeRow.set("payee_id", null);
    }

    async tbMcNumberOnBlur(event: BlurEvent) {
        const tbMcNumber = event.target as Textbox;
        if (event.changedWhileFocused
        && !StringUtil.isEmptyString(tbMcNumber.getDataValue())) {
            const iccNumber = this.mainDataSource.activeRow.get("icc_number", null);
            await this._findSetCarrier(iccNumber, null, null);
            this.assignCarrierBtnEnabled();
            this.displayPrequalResult("MC", iccNumber);
            await this.calculateCarrierRate(this.activeRow);
            this.afterAmountUpdated();
        }
    }

    tbMcNumberOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.clearCarrierAndRate();
        }
    }

    async tbDotNumberOnBlur(event: BlurEvent) {
        const tbDotNumber = event.target as Textbox;
        if (event.changedWhileFocused
        && !StringUtil.isEmptyString(tbDotNumber.getDataValue())) {
            const dotNumber = this.mainDataSource.activeRow.get("dot_number", null);
            await this._findSetCarrier(null, dotNumber, null);
            this.assignCarrierBtnEnabled();
            this.displayPrequalResult("DOT", dotNumber);
            await this.calculateCarrierRate(this.activeRow);
            this.afterAmountUpdated();
        }
    }

    tbDotNumberOnChange(event: ChangeEvent) {
        const tbDotNumber = event.target as Textbox;
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.clearCarrierAndRate();
        }
        this.tbMcNumber.required = StringUtil.isEmptyString(event.newValue);
        this.tbMcNumber.validate(false, false);
    }

    async textboxIntrastateCodeOnBlur(event: BlurEvent) {
        const textboxIntrastateCode = event.target as Textbox;
        if (event.changedWhileFocused
        && !StringUtil.isEmptyString(textboxIntrastateCode.getDataValue())) {
            const intrastate_code = this.mainDataSource.activeRow.get("intrastate_code", null);
            await this._findSetCarrier(null, null, intrastate_code);
            this.assignCarrierBtnEnabled();
            this.displayPrequalResult("CVOR", intrastate_code);
            await this.calculateCarrierRate(this.activeRow);
            this.afterAmountUpdated();
        }
    }

    textboxIntrastateCodeOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.clearCarrierAndRate();
        }
        this.tbMcNumber.required = StringUtil.isEmptyString(event.newValue);
        this.tbMcNumber.validate(false, false);
    }

    clearCarrierAndRate() {
        this.activeRow.setValues({
            "carrier_name": null,
            "payee_id": null,
            "icc_number": null,
            "intrastate_code": null,
            "dot_number": null,
            "amount": null,
            "rating_star_count": null
        });
        this.displayRatingStar(this.activeRow);
        this.mainDataSource.displayDataInBoundComponents();
        this.assignCarrierBtnEnabled();
        this.afterAmountUpdated();
    }

    assignCarrierBtnEnabled() {
        const dotNumber = this.mainDataSource.activeRow.get("dot_number", null);
        const iccNumber = this.mainDataSource.activeRow.get("icc_number", null);
        const intraStateCode = this.mainDataSource.activeRow.get("intrastate_code", null);
        const carrierExists = !StringUtil.isEmptyString(dotNumber) || !StringUtil.isEmptyString(iccNumber) || !StringUtil.isEmptyString(intraStateCode);
        const disabledMessage = !carrierExists ? "Enter carrier information." : "";

        if (!StringUtil.isEmptyString(disabledMessage)) {
            this.buttonAssignCarrier.enabled = disabledMessage.length === 0;
            this.buttonAssignCarrier.disabledTooltip = disabledMessage;
        } else {
            if (this.assignableCheck) {
                this.assignableCheck();
            }
        }
    }

    /** This is an event handler for the onBlur event of csEmptyCity.  */
    csEmptyCityOnBlur(event: BlurEvent) {
        const cityId = this.csEmptyCity?.textCombined?.getFirstLookupModelData()?.get("id");
        const moveId = this.mainDataSource.activeRow?.get("movement_id")
        if (!StringUtil.isEmptyString(cityId) && moveId != null) {
            Api.post("lme/dispatch/get-next-stop-distance", { city_id: cityId, movement_id: moveId }).then(response => {
                this.mainDataSource.activeRow?.set("miles_to_origin", response?.data[0]?.distance);
                this.mainDataSource.displayDataInBoundComponents();
            });
        }
    }

    tbCarrierNameOnDataDisplay(event: DataDisplayEvent) {
        const textbox = event.target as Textbox;
        const modelRow = textbox.getRelevantModelRow();
        textbox.quickInfoOverrideKeyValue = modelRow.get("payee_id");
        this.assignCarrierBtnEnabled();
    }

    tbPayeeIdOnDataDisplay(event:DataDisplayEvent) {
            if(!StringUtil.isEmptyString(event.rowData?.data?.payee_id)) {
                this.sourceCarrier.search({"id": event.rowData?.data?.payee_id}).then(response => {
                    this.panelIsoScorecard.visible = response?.modelRows[0]?.data?.is_iso_active_and_licensed && response?.modelRows[0]?.data?.carrier_scorecard_exported;
                    if (this.mainDataSource.activeRow?.get("carrier_ranking") == null) {
                        this.mainDataSource.activeRow.set("carrier_ranking", response?.modelRows[0]?.data?.carrier_ranking);
                        this.mainDataSource.activeRow.set("rating_star_count", response?.modelRows[0]?.data?.rating_star_count);
                    }
                });
            }
    }

    /** This is an event handler for the onClick event of buttonAssignCarrier.  */
    buttonAssignCarrierOnClick(event: ClickEvent) {
        const modelRow = this.mainDataSource.activeRow;
        if (modelRow == null)
            return;
        const workflowMessage = "Assign Carrier workflow was initiated.";
        const comments = modelRow.get("comments", "");
        modelRow.set("comments", StringUtil.isEmptyString(comments) ? workflowMessage : !comments.includes(workflowMessage) ? comments + "\n" + workflowMessage : comments);
        this.assignButtonClicked();
        this.assignCarrierSlideout(event.target as Button);
    }

    private assignCarrierSlideout(carrierAssignBtn: Button) {
        const carrierOfferInfo: CarrierOfferInfo = {
            payee_id: this.mainDataSource.activeRow?.get("payee_id", null),
            carrier_name: this.mainDataSource.activeRow?.get("carrier_name", ""),
            icc_number: this.mainDataSource.activeRow?.get("icc_number", null),
            dot_number: this.mainDataSource.activeRow?.get("dot_number", null),
            carrier_contact: this.mainDataSource.activeRow?.get("contact_name", ""),
            carrier_phone: this.mainDataSource.activeRow?.get("contact_phone", ""),
            carrier_email: this.mainDataSource.activeRow?.get("email", ""),
            dispatch_offer: this.mainDataSource.activeRow?.getCurrency("amount", null),
            carrier_rate: this.mainDataSource.activeRow?.getCurrency("carrier_rate_calc_pay", this.mainDataSource.activeRow?.getCurrency("carrier_rate", null)),
            counter_offer: this.mainDataSource.activeRow?.getCurrency("counter_offer", null)
        }

        CarrierAssignmentStepper.showAsSlideout({
            movementId: this.mainDataSource.activeRow?.get("movement_id"),
            orderId: this.mainDataSource.activeRow?.get("order_id"),
            carrierAssignmentButton: carrierAssignBtn,
            carrierOfferInfo,
            snackbarTarget: ScreenStack.getPreviousToastTarget(),
            onClose: (canceled) => {
                if (!canceled) {
                    this.carrierAssigned();
                }
            }
        });
    }

    tbContactNameOnBlur(event: BlurEvent) {
        if (this.tbContactName.isEmpty()) {
            this.activeRow.set("contact_phone", null);
            this.activeRow.set("email", null);
        }
    }
}
